<template>
  <div class="quotation-details fade-in">
    <!-- Header -->
    <ProductDetailsHeader
        title="Quotation"
        :main="quotation"
        icon="file-earmark-text"
        :header-details="headerDetails"
    >
    </ProductDetailsHeader>

    <!-- Available Products Section -->
    <StoreProducts
        v-if="quotationDetails.length===0 || showAvailable"
        class="mb-4"
        :per-page="5"
        :marked-products="quotationDetails"
        @row-click="populateAddProduct"
    />

    <!-- Quotation Items -->
    <ProductDetailsPages
        title="Quotation"
        :loading="loading"
        :detail-products="quotationDetails"
        :current-page="detailsPage"
        :current-per-page="detailsPerPage"
        :current-search="detailsSearch"
        @update:page="onDetailsPageChange"
        @update:per-page="onDetailsPerPageChange"
        @update:search="onDetailsSearchChange"
        :main="quotation"
        :pdf-org-title="quotation.customer.name"
        :pdf-org-info="pdfOrgInfo"
        @edit-product="editQuotationProduct"
        @delete-product="deleteQuotationProduct"
    >
      <template #footer-actions>
        <button class="btn btn-outline-success" v-if="quotationDetails.length>0" @click="showAvailable=true">
          <i class="bi bi-plus-circle me-1"></i>Add Quotation Products
        </button>
        <button class="btn btn-outline-info" v-if="quotationDetails.length>0" @click="openConvertModal">
          <i class="bi bi-plus-circle me-1"></i>Convert to Invoice
        </button>
      </template>
    </ProductDetailsPages>

    <!-- Add/Edit Product Modal -->
    <Modal :open="addProductModal" @closed="addProductModal=false" dialog-size="modal-lg">
      <div class="modal-header">
        <h5 class="modal-title m-0" v-if="addProductMode === 'add'">
          <i class="bi bi-plus-circle me-2 text-primary"></i>Add Product to Quotation
        </h5>
        <h5 class="modal-title m-0" v-else>
          <i class="bi bi-pencil-square me-2 text-primary"></i>Edit Quotation Product
        </h5>
        <button @click="addProductModal=false" type="button" class="btn-close"></button>
      </div>
      <div class="modal-body">
        <form @submit.prevent="addProductMode === 'add' ? addQuotationProduct() : updateQuotationProduct()">
          <div class="row">
            <div class="form-group col-md-6 mb-3">
              <label class="form-label fw-medium mb-2">Product Name:</label>
              <input type="text" class="form-control" :value="selectedProduct.product.name" disabled>
            </div>
            <div class="form-group col-md-6 mb-3">
              <label class="form-label fw-medium mb-2">Quantity:</label>
              <div class="input-group">
                <input type="number" class="form-control" autofocus="autofocus" v-on:change="calculateTax" v-model.number="quotationItem.quantity" min="1" required>
              </div>
            </div>
            <div class="form-group col-md-6 mb-3">
              <label class="form-label fw-medium mb-2">Price with tax:</label>
              <div class="input-group">
                <span class="input-group-text">$</span>
                <input type="number" class="form-control" v-model.number="quotationItem.price" v-on:change="calculateTax" min="0" step="0.01" required>
              </div>
              <small class="form-text text-muted">Price without tax: {{ formatNumber((quotationItem.price * quotationItem.quantity) - quotationItem.tax) }}</small>
            </div>
            <div class="form-group col-md-6 mb-3">
              <label class="form-label fw-medium mb-2">Tax:</label>
              <div class="input-group">
                <span class="input-group-text">$</span>
                <input type="number" class="form-control" v-model.number="quotationItem.tax" min="0" step="0.01" disabled>
              </div>
            </div>
            <div class="form-group col-md-6 mb-3">
              <label class="form-label fw-medium mb-2">Total:</label>
              <div class="alert alert-info py-2 mb-0">
                <i class="bi bi-info-circle me-2"></i>
                {{ formatNumber(quotationItem.quantity * quotationItem.price) }}
              </div>
            </div>
          </div>
        </form>
      </div>
      <div class="modal-footer">
        <button class="btn btn-outline-secondary" @click="addProductModal=false">Cancel</button>
        <button class="btn btn-primary" @click="addProductMode === 'add' ? addQuotationProduct() : updateQuotationProduct()">
          <i :class="addProductMode === 'add' ? 'bi bi-plus-lg' : 'bi bi-check-lg'" class="me-1"></i>
          {{ addProductMode === 'add' ? 'Add Product' : 'Update Product' }}
        </button>
      </div>
    </Modal>
    <!-- End Modal -->

    <!-- Convert To Invoice Modal -->
    <Modal :open="convertModal" @closed="convertModal=false">
      <div class="modal-header">
        <h5 class="modal-title m-0">
          <i class="bi bi-arrow-right-circle me-2 text-primary"></i>Convert Quotation to Invoice
        </h5>
        <button @click="convertModal=false" type="button" class="btn-close"></button>
      </div>
      <div class="modal-body">
        <form @submit.prevent="submitConvertToInvoice">
          <div class="mb-3">
            <label class="form-label fw-medium mb-2">Due Date <span class="text-danger">*</span></label>
            <input type="date" v-model="convertForm.due_date" class="form-control" required>
          </div>
          <div class="mb-3">
            <label class="form-label fw-medium mb-2">Notes</label>
            <textarea v-model="convertForm.notes" class="form-control" rows="3" placeholder="Invoice notes"></textarea>
          </div>
        </form>
      </div>
      <div class="modal-footer">
        <button class="btn btn-outline-secondary" @click="convertModal=false">Cancel</button>
        <button class="btn btn-primary" @click="submitConvertToInvoice">
          <i class="bi bi-check-lg me-1"></i>Convert
        </button>
      </div>
    </Modal>
    <!-- End Convert Modal -->
  </div>
</template>

<script>
import quotationService from '@/api/quotationService'
import formatNumber from '@/utils/formatNumber'
import Badge from '@/components/badge/Badge'
import Spinner from '@/components/spinner/Spinner'
import Modal from '@/components/modal/Modal'
import confirmDialog from "@/utils/confirmDialog";
import formatDate from "@/utils/formatDate";
import StoreProducts from '@/components/store/StoreProducts.vue'
import ProductDetailsPages from '@/components/details/ProductDetailsPages.vue'
import ProductDetailsHeader from "@/components/details/ProductDetailsHeader.vue";
import notify from "@/utils/notify";

export default {
  name:'QuotationDetails',
  components:{ProductDetailsHeader, Badge, Spinner, Modal, StoreProducts, ProductDetailsPages },
  data(){
    return {
      loading:true,
      loadingProducts:true,
      quotation:{
        store:{},
        user:{},
        customer:{},
      },
      quotationDetails: { data: [] },
      detailsPage: 1,
      detailsPerPage: 20,
      detailsSearch: '',
      showAvailable:false,
      selectedProduct:{},
      addProductModal:false,
      addProductMode: 'add',
      quotationItem: {
        quotation_id:this.$route.params.id,
        product_id:'',
        quantity:1,
        price:0,
        tax_rate:0,
        tax:0
      },
      editingProductId: null,
      // convert to invoice state
      convertModal:false,
      convertForm:{
        due_date:'',
        notes:''
      }
    }
  },

  methods:{
    formatDate,
    confirmDialog,
    formatNumber,
    async fetchQuotation(){
      try{
        const id = this.$route.params.id;
        this.quotation = await quotationService.getQuotation(id);
        this.quotation.status = 'pending';
        await this.fetchQuotationDetails();
      }catch(e){ console.error(e);} finally { this.loading = false; }
    },
    async fetchQuotationDetails(){
      try{
        const id = this.$route.params.id;
        this.quotationDetails = await quotationService.getQuotationDetails(id, {
          page: this.detailsPage,
          per_page: this.detailsPerPage
        });
      }catch(e){ console.error(e);}
    },
    onDetailsPageChange(page){
      this.detailsPage = page;
      this.fetchQuotationDetails();
    },
    onDetailsPerPageChange(perPage){
      this.detailsPerPage = perPage;
      this.detailsPage = 1;
      this.fetchQuotationDetails();
    },
    onDetailsSearchChange(search){
      this.detailsSearch = search;
      this.detailsPage = 1;
      this.fetchQuotationDetails();
    },
    populateAddProduct(storeProduct){
      this.selectedProduct = storeProduct;
      this.quotationItem.product_id = storeProduct.product.id;
      this.quotationItem.price = storeProduct.price;
      const taxRate = storeProduct.product.tax_category?.rate || 0;
      this.quotationItem.tax_rate = taxRate;
      this.quotationItem.tax = (taxRate / 100) * storeProduct.price * this.quotationItem.quantity;
      this.addProductMode = 'add';
      this.editingProductId = null;
      this.addProductModal=true;
    },
    async addQuotationProduct() {
      try{
        await quotationService.createQuotationDetails(this.quotationItem);
        this.addProductModal=false;
        this.showAvailable=false;
        await this.fetchQuotation();
      }catch(e){console.error(e);}
    },
    async updateQuotationProduct(){
      try{
        if(this.editingProductId) {
          await quotationService.updateQuotationDetails(this.editingProductId, this.quotationItem);
          this.addProductModal=false;
          this.showAvailable=false;
          await this.fetchQuotation();
        }
      }catch(e){console.error(e);}
    },
    editQuotationProduct(item) {
      this.addProductMode = 'edit';
      this.editingProductId = item.id;
      this.selectedProduct = item;
      this.quotationItem = {
        quotation_id: this.$route.params.id,
        product_id: item.product.id,
        quantity: item.quantity,
        price: item.price,
        tax_rate: item.product.tax_category.rate,
        tax: item.tax
      };
      this.addProductModal = true;
    },
    async deleteQuotationProduct(id){
      try{
        await quotationService.deleteQuotationDetails(id);
        await this.fetchQuotation();
      }catch(e){console.error(e);}
    },
    calculateTax() {
      this.quotationItem.tax = (this.quotationItem.tax_rate / 100) * this.quotationItem.price * this.quotationItem.quantity;
    },
    openConvertModal(){
      this.convertForm = {
        due_date: '',
        notes: this.quotation.notes || ''
      }
      this.convertModal = true;
    },
    async submitConvertToInvoice(){
      if(!this.convertForm.due_date){
        notify.alert.error('Please select a due date');
        return;
      }
      try{
        await quotationService.convertToInvoice(this.convertForm, this.quotation.id);
        this.convertModal = false;
      }catch(e){ console.error(e); }
    }
  },

  computed : {
    headerDetails() {
      return [
        { icon: 'person', label: 'Customer', key: 'customer.name' },
        { icon: 'card-text', label: 'Notes', key: 'notes' },
      ];
    },
    pdfOrgInfo() {
      return [
        `${this.quotation.customer?.phone || 'N/A'}`,
        `${this.quotation.customer?.email || 'N/A'}`,
        `${this.quotation.customer?.address || 'N/A'}`,
      ];
    },
  },

  watch: {
    addProductModal(isOpen) {
      if(isOpen) {
        // Focus quantity field on next tick after modal is rendered
        this.$nextTick(() => {
          const quantityInput = document.querySelector('input[v-model\\.number="quotationItem.quantity"]');
          if(quantityInput) quantityInput.focus();
        });
      } else {
        // Reset form when modal is closed
        if(this.addProductMode === 'add') {
          this.quotationItem = {
            quotation_id: this.$route.params.id,
            product_id: '',
            quantity: 1,
            price: 0,
            tax: 0
          };
        }
      }
    },
  },
  async created() {
    await Promise.all([this.fetchQuotation()]);
  }
}
</script>

<style scoped>
</style>
